import { useEffect, useRef, useState } from 'react'
import type { Account } from '../../api/client'
import { SignIn } from '../SignIn'
import { Mark } from '../../components/Mark'
import { Foot, Masthead } from './Chrome'
import '../../styles/landing.css'

/*
  The signed-out homepage.

  Three claims, in this order, because the order is the argument: you are isolated, this
  board argues, and it remembers what you said you would do. Everything else on the page
  is evidence for one of those three.

  Two rules that are easy to break by accident.

  The first is scope. Nothing in a headline or a value claim names an industry, a revenue
  band, a headcount or a fleet size. The seed data this was built against is a plumbing
  company and it kept leaking out here, which disqualifies every visitor who is not that
  company inside a second. Examples are the opposite: concrete, skewed to trades because
  that is the wedge, and a specific question lands harder than an abstract one. Condition
  in the headers, specifics in the evidence.

  The second is that nobody wants an AI product. They want a board. The technology is
  named once, low on the page, as a disclosure. If it climbs back up to the hero, the page
  is selling the wrong thing.
*/

export function Landing({ onSignedIn }: { onSignedIn: (a: Account) => void }) {
  const [route, setRoute] = useState(() => window.location.hash)

  useEffect(() => {
    const on = () => setRoute(window.location.hash)
    window.addEventListener('hashchange', on)
    return () => window.removeEventListener('hashchange', on)
  }, [])

  useReveal()

  if (route === '#signin') return <AuthView onSignedIn={onSignedIn} />

  return (
    <div className="landing">
      <Masthead />
      <Hero />
      <Alone />
      <Argues />
      <Transcript />
      <FollowUp />
      <Cost />
      <Plainly />
      <Close />
      <Foot />
    </div>
  )
}

/* ── Sign in ─────────────────────────────────────────────────────────────── */

function AuthView({ onSignedIn }: { onSignedIn: (a: Account) => void }) {
  return (
    <div className="landing">
      <div className="l-auth">
        <div className="l-auth__pitch">
          <a className="l-lockup" href="#" aria-label="Boardium">
            <Mark variant="on-navy" className="l-lockup__mark" />
            <img className="l-lockup__word" src="/brand/wordmark-white.png" alt="Boardium" />
          </a>
          <div>
            <h2 className="l-h2">The board is already sitting.</h2>
            <p>
              Six items on the table, one commitment overdue, and a figure from last month
              that two sources disagree about.
            </p>
          </div>
          <Mark variant="on-navy" className="l-auth__mark" />
        </div>

        <div className="l-auth__form">
          <div>
            <a className="l-auth__back" href="#">
              <Arrow flip /> Back to the homepage
            </a>
            <a className="l-lockup l-auth__brand" href="#" aria-label="Boardium">
              <Mark className="l-lockup__mark" />
              <img className="l-lockup__word" src="/brand/wordmark-ink.png" alt="Boardium" />
            </a>
            {/* The heading moved inside SignIn: it changes with the mode, and a
                panel that says "Sign in" above a signup form is worse than none. */}
            <SignIn onSignedIn={onSignedIn} />
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── Hero ────────────────────────────────────────────────────────────────── */

/*
  The hero shows the product rather than an illustration of it. A recommendation with a
  vote count and a named objection underneath is the strongest thing this does, and it is
  also the whole answer to "why not just ask a chatbot". Showing it costs one card.
  Describing it costs a paragraph nobody reads.
*/
function Hero() {
  return (
    <section className="l-hero" id="top">
      <div className="l-wrap">
        <div className="l-hero__grid">
          <div>
            <h1 className="l-h1 l-hero__h1" data-reveal>
              You're the smartest person in the room.{' '}
              <em>That's the problem.</em>
            </h1>
            <p className="l-lede" data-reveal style={{ '--d': '90ms' } as React.CSSProperties}>
              The people who could actually push back on you run five grand a month. This
              board sits every Friday, argues in front of you, and writes down who
              disagreed.
            </p>
            {/* "30 days" is Stripe:TrialDays on the app pool, and the two have to move
                together: this page is the offer and that setting is what Checkout actually
                does. The masthead keeps "Sign in" for somebody who already has an account. */}
            <div className="l-hero__actions" data-reveal style={{ '--d': '160ms' } as React.CSSProperties}>
              <a className="l-btn l-btn--gold l-btn--lg" href="#signin">
                Start 30 days free <Arrow />
              </a>
              <a className="l-btn l-btn--line l-btn--lg" href="#meeting">Read a full meeting</a>
            </div>
          </div>

          <div className="l-hero__art" data-reveal style={{ '--d': '120ms' } as React.CSSProperties}>
            <Verdict />
            <AppStore className="l-appstore--hero" delay="220ms" />
          </div>
        </div>
      </div>
    </section>
  )
}

/* The product at the size it actually reads: a decision with the objection attached. */
function Verdict() {
  return (
    <article className="l-verdict">
      <p className="l-verdict__q">My best tech just asked for a 20% raise.</p>

      <div className="l-verdict__seats">
        {[
          ['cfo', 'Priya Raghavan', 'Match it and you have repriced the whole crew by March.'],
          ['trade_veteran', 'Manny Ortiz', 'Lose him and two more follow him inside a quarter.'],
          ['skeptic', 'Sam Whitfield', 'Nothing here says he has another offer in hand.'],
        ].map(([key, name, line]) => (
          <div className="l-vseat" key={key}>
            <img
              src={`/avatars/${key}.webp`}
              srcSet={`/avatars/${key}.webp 1x, /avatars/${key}@2x.webp 2x`}
              alt="" loading="lazy" decoding="async"
            />
            <div>
              <span className="l-vseat__name">{name}</span>
              <span className="l-vseat__line">{line}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="l-verdict__out">
        <span className="l-verdict__vote">Carried, three to one</span>
        <p>
          Counter at 12% starting October 1st, tied to him running the apprentice program.
          Revisit the whole wage talks in January rather than one person at a time.
        </p>
      </div>

      <div className="l-verdict__dissent">
        <span className="l-verdict__dlabel">Recorded dissent, Sam Whitfield</span>
        <p>You have taught the crew that threatening to leave works. Expect the second one by March.</p>
      </div>
    </article>
  )
}

/* ── The condition ───────────────────────────────────────────────────────── */

function Alone() {
  return (
    <section className="l-section" id="alone">
      <div className="l-wrap l-cols">
        <p className="l-eyebrow l-rail">The problem</p>
        <div className="l-alone">
          <div>
            <h2 className="l-h2" data-reveal>You are the only one at your level.</h2>

            <p className="l-body" data-reveal style={{ '--d': '100ms', marginTop: 20 } as React.CSSProperties}>
              Your operations manager works for you, so they aren't going to tell you the
              second location is a mistake. Your accountant answers the question you asked
              and then stops. Your partner didn't sign up to argue about gross margin at
              eleven at night.
            </p>
            <p className="l-stress l-stress--bold" data-reveal style={{ '--d': '130ms' } as React.CSSProperties}>
              So the biggest decision of the quarter gets made by one person.
            </p>
            <p className="l-body" data-reveal style={{ '--d': '160ms' } as React.CSSProperties}>
              Every company above a certain size solves this the same way. It appoints
              people who know the business, aren't on the payroll, and will say the thing
              out loud. That's what boards do. It's also the one piece of company
              infrastructure that never came down in price. Until now.
            </p>
          </div>

          {/* Decorative: the copy already makes this point, so it carries an empty alt. */}
          <img
            className="l-alone__art"
            src="/illustrations/alone-table.webp"
            srcSet="/illustrations/alone-table.webp 1x, /illustrations/alone-table@2x.webp 2x"
            width={340}
            height={550}
            alt=""
            loading="lazy"
            decoding="async"
            data-reveal
            style={{ '--d': '190ms' } as React.CSSProperties}
          />
        </div>
      </div>
    </section>
  )
}

/* ── It argues ───────────────────────────────────────────────────────────── */

type Seat = {
  key: string
  name: string
  role: string
  r1: string
  r2: string
  chair?: boolean
  dissent?: boolean
}

const SEATS: Seat[] = [
  {
    key: 'wartime_ceo', name: 'Dana Reyes', role: 'Chair', chair: true,
    r1: 'Put the increase in before the season turns. Waiting is the expensive option.',
    r2: 'Holds. Gives Priya the number and keeps the date.',
  },
  {
    key: 'trade_veteran', name: 'Manny Ortiz', role: 'Operator',
    r1: 'Your two best people walk by March if the schedule stays like this.',
    r2: 'Unmoved. Says the price is the smaller of the two problems.',
  },
  {
    key: 'cfo', name: 'Priya Raghavan', role: 'CFO',
    r1: 'Gross margin is 31 percent. In March it was 38. Find those seven points first.',
    r2: 'Revises. Backs the increase at 6 percent, not 12.',
  },
  {
    key: 'skeptic', name: 'Sam Whitfield', role: 'Dissenter', dissent: true,
    r1: 'Nothing on the table says these customers will pay it.',
    r2: 'Argues the whole case against. Still against when the round closes.',
  },
  {
    key: 'operator_gm', name: 'Ruth Kellerman', role: 'General manager',
    r1: 'Throughput is the constraint here, not price.',
    r2: 'Moves to Priya. Wants the margin work sequenced ahead of the rise.',
  },
]

const ROUNDS = [
  {
    tab: 'Isolated',
    note: 'Five calls leave at the same moment. None of them carries the other four positions, or the fact that you clearly want a yes.',
  },
  {
    tab: 'Challenged',
    note: 'Everyone now sees everything. The dissenting seat has a standing mandate to argue the strongest case against wherever the room is heading, whatever it said in round one.',
  },
  {
    tab: 'The chair',
    note: 'One call with all of it in front of them. On keeping people the operator outweighs the marketer. On ad spend it runs the other way, and the chair is handed the weights.',
  },
  {
    tab: 'Commitments',
    note: 'Owners, dates, and a metric. A commitment with a 30 day metric puts itself back on the agenda the week that metric can be checked.',
  },
]

const BROUGHT = [
  'My best tech just asked for a 20% raise.',
  'A customer wants net-60 terms on a $40k job.',
  'Should I open a second location or go deeper in this one?',
  'My top salesperson is threatening to leave and take accounts.',
]

function Argues() {
  const [round, setRound] = useState(1)
  const [held, setHeld] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (held) return
    const el = box.current
    if (!el) return
    let visible = false
    const io = new IntersectionObserver(([e]) => { visible = !!e?.isIntersecting }, { threshold: 0.35 })
    io.observe(el)
    const id = setInterval(() => { if (visible) setRound(r => (r % 4) + 1) }, 5200)
    return () => { io.disconnect(); clearInterval(id) }
  }, [held])

  return (
    <section className="l-section" id="argues">
      <div className="l-wrap l-cols">
        <p className="l-eyebrow l-rail">How it argues</p>
        <div>
          <h2 className="l-h2" data-reveal>The argument is the product.</h2>

          <div className="l-argue">
            <div className="l-argue__text" data-reveal
                 style={{ '--d': '110ms' } as React.CSSProperties}>
              <p className="l-body">
                Round one. All five answer at once, each blind to the others. Cold takes, no
                anchoring.
              </p>
              <p className="l-body">
                Round two. Now they see the room. Positions get argued out of people, or
                held under pressure. The seat you built to disagree earns its keep.
              </p>
              <p className="l-body">
                The chair. Weights each voice by whose ground the question sits on. Writes
                the minutes.
              </p>
              <p className="l-body">
                What survives is a decision with a vote count and a named objection
                attached.
              </p>
              <p className="l-stress l-stress--bold">
                A board that comes back unanimous every week is a mirror.
              </p>
            </div>

            {/*
              Decorative: the paragraphs beside it already say this, and the file carries
              its own <title> and <desc> which an <img> does not expose anyway.

              Its own background rect is #F4F5F7, the same as --l-paper, so the picture has
              no visible edge against the section.
            */}
            <img
              className="l-argue__art"
              src="/illustrations/deliberation-diagram.svg"
              width={400}
              height={540}
              alt=""
              loading="lazy"
              decoding="async"
              data-reveal
              style={{ '--d': '150ms' } as React.CSSProperties}
            />
          </div>

          <div className="l-rounds" data-reveal style={{ '--d': '160ms' } as React.CSSProperties} ref={box}>
            <div className="l-rounds__tabs" role="tablist" aria-label="Deliberation rounds">
              {ROUNDS.map((r, i) => (
                <button
                  key={r.tab}
                  className="l-tab"
                  role="tab"
                  aria-selected={round === i + 1}
                  onClick={() => { setRound(i + 1); setHeld(true) }}
                >
                  <span className="l-tab__n">Round {i + 1}</span>
                  <span className="l-tab__t">{r.tab}</span>
                </button>
              ))}
            </div>

            <div className="l-room" data-round={round}>
              <div className="l-room__head">
                <p>{ROUNDS[round - 1]!.note}</p>
              </div>

              <div className="l-seats">
                <svg className="l-wires" viewBox="0 0 100 44" preserveAspectRatio="none" aria-hidden>
                  <path d="M10 40 Q30 6 50 40" vectorEffect="non-scaling-stroke" />
                  <path d="M30 40 Q50 8 70 40" vectorEffect="non-scaling-stroke" />
                  <path d="M50 40 Q70 6 90 40" vectorEffect="non-scaling-stroke" />
                  <path d="M10 40 Q50 2 90 40" vectorEffect="non-scaling-stroke" />
                </svg>

                {SEATS.map(s => (
                  <article
                    key={s.key}
                    className={`l-seat${s.chair ? ' l-seat--chair' : ''}${s.dissent ? ' l-seat--dissent' : ''}`}
                    data-seat={s.name}
                  >
                    <div className="l-seat__face">
                      <img
                        src={`/avatars/${s.key}.webp`}
                        srcSet={`/avatars/${s.key}.webp 1x, /avatars/${s.key}@2x.webp 2x`}
                        alt="" loading="lazy" decoding="async"
                      />
                    </div>
                    <h3 className="l-seat__name">{s.name}</h3>
                    <p className="l-seat__role">{s.role}</p>
                    <div className="l-sealed" aria-hidden>
                      <i /><i /><i />
                      <span>Position withheld</span>
                    </div>
                    <p className="l-seat__line">{round === 1 ? s.r1 : s.r2}</p>
                    <span className="l-seat__tag">Dissent</span>
                  </article>
                ))}
              </div>
            </div>
          </div>

          <div className="l-brought" data-reveal>
            <p className="l-eyebrow">What lands on a Friday</p>
            <ul>
              {BROUGHT.map(q => <li key={q}>{q}</li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── One meeting, at length ──────────────────────────────────────────────── */

/*
  Long-form and unabridged. The output is the best asset the page has and putting it
  behind a row of feature cards wastes it. Anyone still reading here is deciding whether
  the thing is any good, and the only honest way to answer that is to let them read what
  it actually said.
*/
const TURNS: [string, string, string, string[]][] = [
  ['cfo', 'Priya Raghavan', 'CFO', [
    'Forty thousand at net-60 is sixty days of somebody else holding your money. On your current position that is most of a month of payroll. The question is not whether they are good for it. It is whether you can carry the gap without a line of credit you do not have.',
    'Run it as two invoices. Half at material delivery, half at completion, both net-30. If they refuse a payment schedule on a forty thousand dollar job, that is information about how the next one goes.',
  ]],
  ['trade_veteran', 'Manny Ortiz', 'Operator', [
    'I have been the one carrying the big logo before. You get the job, you get the terms, and then you get the change orders nobody signs for because the relationship is good. Then you are ninety days out, not sixty.',
    'Take it. Put somebody on it who writes things down.',
  ]],
  ['skeptic', 'Sam Whitfield', 'Dissenter', [
    'Everyone here is treating "the biggest name on my client list" as a reason to say yes. It is the reason to be careful. A client that size has a payables department that pays on its own schedule regardless of what it agreed with you, and you will not want to chase them because you want the next job.',
  ]],
]

function Transcript() {
  return (
    <section className="l-section" id="meeting">
      <div className="l-wrap l-cols">
        <p className="l-eyebrow l-rail">One meeting</p>
        <div>
          <h2 className="l-h2" data-reveal>One question, and what came back.</h2>
          <p className="l-body" data-reveal style={{ '--d': '100ms', marginTop: 20 } as React.CSSProperties}>
            The board opinions come in citing pros and cons. A debate then a
            recommendation.
          </p>

          <div className="l-script" data-reveal style={{ '--d': '140ms' } as React.CSSProperties}>
            <p className="l-script__q">
              <span className="l-eyebrow">On the table</span>
              A customer wants net-60 terms on a $40k job. They are good for it and they are
              the biggest name on my client list.
            </p>

            {TURNS.map(([key, name, role, lines]) => (
              <div className="l-turn" key={key}>
                <div className="l-turn__who">
                  <img
                    src={`/avatars/${key}.webp`}
                    srcSet={`/avatars/${key}.webp 1x, /avatars/${key}@2x.webp 2x`}
                    alt="" loading="lazy" decoding="async"
                  />
                  <div>
                    <span className="l-turn__name">{name}</span>
                    <span className="l-turn__role">{role}</span>
                  </div>
                </div>
                <div className="l-turn__body">
                  {lines.map((t, i) => <p key={i}>{t}</p>)}
                </div>
              </div>
            ))}

            <div className="l-minutes">
              <p className="l-minutes__vote">Carried, four to one.</p>
              <p>
                Take the job. Two invoices, both net-30, split at material delivery and
                completion. Written change order policy agreed before the first invoice goes
                out, and a named person on site who logs variations the day they happen.
              </p>
              <div className="l-dissent">
                <span className="l-dissent__label">Recorded dissent</span>
                <p>
                  Sam Whitfield: the board solved the terms and left the real risk alone. If
                  this client pays the first invoice more than fifteen days late, the second
                  job goes on deposit or it does not go at all.
                </p>
              </div>
            </div>

            <div className="l-commits">
              {[
                ['Put the change order policy in writing and send it before invoicing', 'You, 7 days', 'Metric: policy sent and acknowledged'],
                ['Name the person logging variations on site', 'You, 7 days', 'Metric: named in writing to the client'],
                ['Check the first invoice against its due date', 'Priya, 45 days', 'Metric: days late, recorded'],
              ].map(([what, who, metric]) => (
                <div className="l-commit" key={what}>
                  <p className="l-commit__what">{what}</p>
                  <span className="l-commit__meta">{who}</span>
                  <span className="l-commit__metric">{metric}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── It follows up ───────────────────────────────────────────────────────── */

function FollowUp() {
  return (
    <section className="l-section l-band" id="after">
      <Mark variant="on-navy" className="l-band__mark" />
      <div className="l-wrap l-cols">
        <p className="l-eyebrow l-rail">After the meeting</p>
        <div>
          <h2 className="l-h2" data-reveal>Action items then follow-up.</h2>

          <p className="l-body" data-reveal style={{ '--d': '100ms', marginTop: 20 } as React.CSSProperties}>
            You said you'd let the site manager go. Did you? Nobody else in your life asks
            that, because nobody else was in the room when you said it and nobody else wrote
            down the date.
          </p>
          <p className="l-body" data-reveal style={{ '--d': '130ms' } as React.CSSProperties}>
            Every commitment leaves the meeting with an owner, a date, and a way to check
            it. Forty-five days later it comes back onto the agenda on its own and somebody
            asks. If it slipped, that goes down too. The board is still there in April to
            notice that the thing promised in March didn't happen. A clever stranger in a
            single conversation can't do that.
          </p>
          <p className="l-body" data-reveal style={{ '--d': '160ms' } as React.CSSProperties}>
            It's also why month six beats month one. Last quarter's argument is in the room
            when this quarter's version comes back. So is the decision you made against the
            dissent, and how it turned out.
          </p>
        </div>
      </div>
    </section>
  )
}

/* ── Price ───────────────────────────────────────────────────────────────── */

/*
  Anchored against people rather than software, because the thing being replaced is a
  person in a chair. $249 is BoardValidator.MonthlyPrice; if pricing moves, both have to.

  The thirty days is Stripe:TrialDays on the app pool. Same rule, and it bites harder: a
  page promising a month free while Checkout writes a fourteen day trial bills somebody
  sixteen days early, and they find out from their statement.
*/
function Cost() {
  return (
    <section className="l-section" id="cost">
      <div className="l-wrap l-cols">
        <p className="l-eyebrow l-rail">What it costs</p>
        <div>
          <h2 className="l-h2" data-reveal>$249/month for a board worth $30K/month. You do the math.</h2>

          <div className="l-price" data-reveal style={{ '--d': '110ms' } as React.CSSProperties}>
            <div className="l-price__row">
              <span className="l-price__what">A fractional CFO</span>
              <span className="l-price__amt">$3,000 to $8,000 a month</span>
              <span className="l-price__note">One opinion, and often a good one. Still one.</span>
            </div>
            <div className="l-price__row">
              <span className="l-price__what">A seat on a real advisory board</span>
              <span className="l-price__amt">A retainer, usually plus equity</span>
              <span className="l-price__note">They meet quarterly. You have decisions weekly.</span>
            </div>
            <div className="l-price__row l-price__row--ours">
              <span className="l-price__what">Boardium</span>
              <span className="l-price__amt">Free for 30 days, then $249 a month</span>
              <span className="l-price__note">Five seats, a meeting every Friday, everything said kept.</span>
            </div>
          </div>

          <p className="l-body" data-reveal style={{ marginTop: 28 } as React.CSSProperties}>
            One plan, and no per-seat pricing. A board with fewer seats is not a cheaper
            board. It is a worse one.
          </p>

          {/* The qualifier belongs next to the offer, not in a footnote somebody finds
              after the charge. Starting takes a card: that is the actual deal and saying
              so here is cheaper than saying it in a refund email. */}
          <p className="l-body" data-reveal style={{ marginTop: 16 } as React.CSSProperties}>
            The first thirty days cost nothing. Starting takes a card, the first charge
            lands on day 31, and cancelling before then means you are not billed at all.
            Four meetings is enough to know whether a board that argues in front of you is
            worth keeping.
          </p>

          <AppStore />
        </div>
      </div>
    </section>
  )
}

/*
  The iPhone app, before there is an iPhone app.

  Apple's badge, unmodified and served from our own origin, which is what their marketing
  guidelines ask for. It is not a link: the app is with Apple and there is nothing on the
  other end yet, and a badge that navigates nowhere is worse than one that plainly does not.
  So it is a static image with the state written underneath it, and it becomes an anchor the
  day the listing exists.

  Twice: under the verdict card in the hero, and under the price. Both are places where
  somebody has just seen what the product actually produces, which is the moment the phone
  app is worth mentioning. Neither is in the headline, because what is being sold is a board
  that meets weekly and the rule this page is built on is that the technology never climbs
  above the argument.
*/
function AppStore({ className, delay }: { className?: string; delay?: string }) {
  return (
    <div
      className={`l-appstore${className ? ` ${className}` : ''}`}
      data-reveal
      style={delay ? ({ '--d': delay } as React.CSSProperties) : undefined}
    >
      <img
        className="l-appstore__badge"
        src="/brand/app-store-badge.svg"
        alt="Download on the App Store"
        width={140}
        height={47}
      />
      <span className="l-appstore__note">Coming soon</span>
    </div>
  )
}

/* ── What it is, said out loud ───────────────────────────────────────────── */

/*
  The technology gets named here and nowhere else. A page that sells business advice and
  never says who is giving it is doing something dishonest, and business advice is a
  subject where that matters more than most.
*/
function Plainly() {
  return (
    <section className="l-section" id="plainly">
      <div className="l-wrap l-cols">
        <p className="l-eyebrow l-rail">Said plainly</p>
        <div>
          <h2 className="l-h2" data-reveal>Model the perfect board member for your business.</h2>

          <p className="l-body" data-reveal style={{ '--d': '100ms', marginTop: 20 } as React.CSSProperties}>
            Nobody on this board is a person. Each seat is built from nineteen attributes
            you set, and none of them is an accountant, a lawyer, a broker or a licensed
            advisor of any kind. When a question turns on tax, employment law or insurance,
            the seat answers it as well as it can and then says plainly that the answer is
            not a substitute for your attorney or your tax professional.
          </p>

          <blockquote className="l-quote l-quote--danger" data-reveal style={{ '--d': '140ms' } as React.CSSProperties}>
            Document the incident today, notify your carrier inside the window your policy
            sets, and keep him on payroll until you have written guidance. That is my read of
            it, and it is not legal advice: put it in front of your attorney and your broker
            before you act on any of it.
            <cite>A seat closing a consult about an injured employee, 28 August</cite>
          </blockquote>

          <div className="l-note" data-reveal>
            <div>
              <h3 className="l-h3">Where the answers come from.</h3>
              <p className="l-body" style={{ marginTop: 12 }}>
                Your own figures, your own documents, and the decisions this board has
                already recorded for you. Nothing about your business is ever used to answer
                anyone else's question.
              </p>
            </div>
            <div>
              <h3 className="l-h3">It can be wrong, and it has to say when.</h3>
              <p className="l-body" style={{ marginTop: 12 }}>
                A member short of a number is told to estimate it, say what it assumed, and
                name the single figure that would settle the question. Check that one thing
                rather than taking the whole answer on trust.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── Close ───────────────────────────────────────────────────────────────── */

function Close() {
  return (
    <section className="l-section l-close">
      <div className="l-wrap">
        <h2 className="l-h2" data-reveal>The board meets every Friday with or without you.</h2>
        <p className="l-lede" data-reveal style={{ '--d': '70ms' } as React.CSSProperties}>
          Will you be there?
        </p>
        <div className="l-close__actions" data-reveal style={{ '--d': '130ms' } as React.CSSProperties}>
          <a className="l-btn l-btn--gold l-btn--lg" href="#signin">Start 30 days free <Arrow /></a>
          <a className="l-btn l-btn--line l-btn--lg" href="#meeting">Read a full meeting</a>
        </div>
      </div>
    </section>
  )
}

/* ── Bits ────────────────────────────────────────────────────────────────── */

function Arrow({ flip }: { flip?: boolean }) {
  return (
    <svg
      className="l-btn__arrow" width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={flip ? { transform: 'rotate(180deg)' } : undefined} aria-hidden
    >
      <path d="M5 12h13M12 5.5 18.5 12 12 18.5" />
    </svg>
  )
}

/*
  One observer for the whole page. Reveals are one-way: something already read should not
  fade out again when it leaves the viewport.
*/
function useReveal() {
  useEffect(() => {
    const nodes = document.querySelectorAll('[data-reveal]')
    if (!nodes.length) return

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach(n => n.classList.add('is-in'))
      return
    }

    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target) }
      }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    )
    nodes.forEach(n => io.observe(n))
    return () => io.disconnect()
  })
}
